import { style, styleVariants } from '@vanilla-extract/css';
import {
  componentSpecificTokenVars,
  semanticTokenVars,
} from '@tundr/ds-tokens';
import {
  fontFamilyVars,
  fontWeightVars,
  fontSizeVars,
  lineHeightVars,
  spacingVars,
  radiusVars,
  borderWidthVars,
} from '@tundr/ds-foundations';
import {
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  type ButtonColorType,
  type ButtonVariantType,
} from './Button.types';

import { BASE_TRANSITION as TRANSITION_DURATION } from '../_shared/styles/motion';
const buttonTokens = componentSpecificTokenVars.button;
const SOLID_SM_HEIGHT = '32px';
const enabledSelector = '&:hover:not(:disabled):not([aria-disabled="true"])';
const activeEnabledSelector =
  '&:active:not(:disabled):not([aria-disabled="true"]), &[data-active="true"]:not(:disabled):not([aria-disabled="true"])';
const disabledSelector = '&:disabled, &[aria-disabled="true"]';

export const baseClass = style({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: spacingVars.sm,
  minWidth: '130px',
  height: '48px',
  padding: spacingVars.md,
  borderRadius: radiusVars.md,
  fontFamily: fontFamilyVars.body,
  fontWeight: fontWeightVars.bold,
  fontSize: fontSizeVars.body.body1,
  lineHeight: lineHeightVars.body.body1,
  letterSpacing: '0',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  border: 'none',
  textDecoration: 'none',
  transition: `background-color ${TRANSITION_DURATION}, border-color ${TRANSITION_DURATION}, color ${TRANSITION_DURATION}`,
  selectors: {
    '&:focus-visible': {
      outline: `${borderWidthVars.xs} solid ${semanticTokenVars.border.brand.base}`,
      outlineOffset: '1px',
    },
    [disabledSelector]: {
      cursor: 'not-allowed',
    },
  },
});

const outlineVariantStyle = {
  backgroundColor: 'transparent',
  color: buttonTokens.secondary.default.content.default,
  borderStyle: 'solid',
  borderWidth: borderWidthVars.xs,
  borderColor: buttonTokens.secondary.default.border.default,
  selectors: {
    [enabledSelector]: {
      backgroundColor: buttonTokens.secondary.default.fill.hover,
    },
    [activeEnabledSelector]: {
      backgroundColor: buttonTokens.secondary.default.fill.pressed,
    },
    [disabledSelector]: {
      borderColor: buttonTokens.secondary.default.border.disabled,
      color: buttonTokens.secondary.default.content.disabled,
    },
  },
} as const;

/** Shared structural base for Text and Inline variants (tone applied on top). */
const textLikeBase = {
  backgroundColor: 'transparent',
  border: 'none',
  textDecoration: 'none',
  fontWeight: fontWeightVars.regular,
  gap: spacingVars.sm,
} as const;

export const variantClasses = styleVariants({
  [ButtonVariant.Primary]: {
    backgroundColor: buttonTokens.primary.default.default,
    color: buttonTokens.primary.default.content.default,
    selectors: {
      [enabledSelector]: {
        backgroundColor: buttonTokens.primary.default.hover,
      },
      [activeEnabledSelector]: {
        backgroundColor: buttonTokens.primary.default.pressed,
      },
      [disabledSelector]: {
        backgroundColor: buttonTokens.primary.default.disabled,
        color: buttonTokens.primary.default.content.disabled,
      },
    },
  },
  [ButtonVariant.Secondary]: outlineVariantStyle,
  [ButtonVariant.Icon]: {
    backgroundColor: 'transparent',
    color: buttonTokens.iconButton.default.content.default,
    border: `${borderWidthVars.xs} solid ${buttonTokens.iconButton.default.border.default}`,
    selectors: {
      [enabledSelector]: {
        backgroundColor: buttonTokens.iconButton.default.fill.hover,
      },
      [activeEnabledSelector]: {
        backgroundColor: buttonTokens.iconButton.default.fill.pressed,
      },
      [disabledSelector]: {
        borderColor: buttonTokens.iconButton.default.border.disabled,
        color: buttonTokens.iconButton.default.content.disabled,
      },
    },
  },
  [ButtonVariant.Text]: {
    ...textLikeBase,
    color: buttonTokens.textButton.secondary.content.default,
    selectors: {
      [enabledSelector]: {
        backgroundColor: buttonTokens.textButton.secondary.fill.hover,
      },
      [activeEnabledSelector]: {
        backgroundColor: buttonTokens.textButton.secondary.fill.pressed,
      },
      [disabledSelector]: {
        color: buttonTokens.textButton.secondary.content.disabled,
      },
    },
  },
  [ButtonVariant.Inline]: {
    ...textLikeBase,
    minWidth: 'auto',
    height: 'auto',
    padding: spacingVars.none,
    color: buttonTokens.inlineButton.secondary.default,
    selectors: {
      [enabledSelector]: {
        color: buttonTokens.inlineButton.secondary.hover,
      },
      [activeEnabledSelector]: {
        color: buttonTokens.inlineButton.secondary.pressed,
      },
      [disabledSelector]: {
        color: buttonTokens.inlineButton.secondary.disabled,
      },
    },
  },
});

/*
 * Tone modifier classes. Defined AFTER variantClasses so they win
 * in the CSS cascade.
 */

const primaryDangerClass = style({
  backgroundColor: buttonTokens.primary.danger.default,
  color: buttonTokens.primary.danger.content.default,
  selectors: {
    [enabledSelector]: {
      backgroundColor: buttonTokens.primary.danger.hover,
    },
    [activeEnabledSelector]: {
      backgroundColor: buttonTokens.primary.danger.pressed,
    },
    [disabledSelector]: {
      backgroundColor: buttonTokens.primary.danger.disabled,
      color: buttonTokens.primary.danger.content.disabled,
    },
  },
});

const primaryOnBrandClass = style({
  backgroundColor: buttonTokens.primary.onBrand.default,
  color: buttonTokens.primary.onBrand.content.default,
  selectors: {
    [enabledSelector]: {
      backgroundColor: buttonTokens.primary.onBrand.hover,
      color: buttonTokens.primary.onBrand.content.hover,
    },
    [activeEnabledSelector]: {
      backgroundColor: buttonTokens.primary.onBrand.pressed,
      color: buttonTokens.primary.onBrand.content.pressed,
    },
    [disabledSelector]: {
      backgroundColor: buttonTokens.primary.onBrand.disabled,
      color: buttonTokens.primary.onBrand.content.disabled,
    },
  },
});

const secondaryDangerClass = style({
  color: buttonTokens.secondary.danger.content.default,
  borderColor: buttonTokens.secondary.danger.border.default,
  selectors: {
    [enabledSelector]: {
      backgroundColor: buttonTokens.secondary.danger.fill.hover,
    },
    [activeEnabledSelector]: {
      backgroundColor: buttonTokens.secondary.danger.fill.pressed,
    },
    [disabledSelector]: {
      borderColor: buttonTokens.secondary.danger.border.disabled,
      color: buttonTokens.secondary.danger.content.disabled,
    },
  },
});

const secondaryOnBrandClass = style({
  color: buttonTokens.secondary.onBrand.content.default,
  borderColor: buttonTokens.secondary.onBrand.border.default,
  selectors: {
    [enabledSelector]: {
      backgroundColor: buttonTokens.secondary.onBrand.fill.hover,
    },
    [activeEnabledSelector]: {
      backgroundColor: buttonTokens.secondary.onBrand.fill.pressed,
    },
    [disabledSelector]: {
      borderColor: buttonTokens.secondary.onBrand.border.disabled,
      color: buttonTokens.secondary.onBrand.content.disabled,
    },
  },
});

const iconDangerClass = style({
  color: buttonTokens.iconButton.danger.content.default,
  border: `${borderWidthVars.xs} solid ${buttonTokens.iconButton.danger.border.default}`,
  selectors: {
    [enabledSelector]: {
      backgroundColor: buttonTokens.iconButton.danger.fill.hover,
    },
    [activeEnabledSelector]: {
      backgroundColor: buttonTokens.iconButton.danger.fill.pressed,
    },
    [disabledSelector]: {
      borderColor: buttonTokens.iconButton.danger.border.disabled,
      color: buttonTokens.iconButton.danger.content.disabled,
    },
  },
});

const iconOnBrandClass = style({
  color: buttonTokens.iconButton.onBrand.content.default,
  border: `${borderWidthVars.xs} solid ${buttonTokens.iconButton.onBrand.border.default}`,
  selectors: {
    [enabledSelector]: {
      backgroundColor: buttonTokens.iconButton.onBrand.fill.hover,
    },
    [activeEnabledSelector]: {
      backgroundColor: buttonTokens.iconButton.onBrand.fill.pressed,
    },
    [disabledSelector]: {
      borderColor: buttonTokens.iconButton.onBrand.border.disabled,
      color: buttonTokens.iconButton.onBrand.content.disabled,
    },
    '&:focus-visible': {
      outlineColor: semanticTokenVars.border.onBrand.default,
    },
  },
});

const textDefaultClass = style({
  color: buttonTokens.textButton.default.content.default,
  selectors: {
    [enabledSelector]: {
      backgroundColor: buttonTokens.textButton.default.fill.hover,
    },
    [activeEnabledSelector]: {
      backgroundColor: buttonTokens.textButton.default.fill.pressed,
    },
    [disabledSelector]: {
      color: buttonTokens.textButton.default.content.disabled,
    },
  },
});

const textDangerClass = style({
  color: buttonTokens.textButton.danger.content.default,
  selectors: {
    [enabledSelector]: {
      backgroundColor: buttonTokens.textButton.danger.fill.hover,
    },
    [activeEnabledSelector]: {
      backgroundColor: buttonTokens.textButton.danger.fill.pressed,
    },
    [disabledSelector]: {
      color: buttonTokens.textButton.danger.content.disabled,
    },
  },
});

const textOnBrandClass = style({
  color: buttonTokens.textButton.onBrand.content.default,
  selectors: {
    [enabledSelector]: {
      backgroundColor: buttonTokens.textButton.onBrand.fill.hover,
    },
    [activeEnabledSelector]: {
      backgroundColor: buttonTokens.textButton.onBrand.fill.pressed,
    },
    [disabledSelector]: {
      color: buttonTokens.textButton.onBrand.content.disabled,
    },
  },
});

/* Inline tones shift the content color on interaction (no fill). */

const inlineDefaultClass = style({
  color: buttonTokens.inlineButton.default.default,
  selectors: {
    [enabledSelector]: {
      color: buttonTokens.inlineButton.default.hover,
    },
    [activeEnabledSelector]: {
      color: buttonTokens.inlineButton.default.pressed,
    },
    [disabledSelector]: {
      color: buttonTokens.inlineButton.default.disabled,
    },
  },
});

const inlineDangerClass = style({
  color: buttonTokens.inlineButton.danger.default,
  selectors: {
    [enabledSelector]: {
      color: buttonTokens.inlineButton.danger.hover,
    },
    [activeEnabledSelector]: {
      color: buttonTokens.inlineButton.danger.pressed,
    },
    [disabledSelector]: {
      color: buttonTokens.inlineButton.danger.disabled,
    },
  },
});

const inlineOnBrandClass = style({
  color: buttonTokens.inlineButton.onBrand.default,
  selectors: {
    [enabledSelector]: {
      color: buttonTokens.inlineButton.onBrand.hover,
    },
    [activeEnabledSelector]: {
      color: buttonTokens.inlineButton.onBrand.pressed,
    },
    [disabledSelector]: {
      color: buttonTokens.inlineButton.onBrand.disabled,
    },
  },
});

export const colorClassesByVariant: Record<
  ButtonVariantType,
  Partial<Record<ButtonColorType, string>>
> = {
  [ButtonVariant.Primary]: {
    [ButtonColor.Danger]: primaryDangerClass,
    [ButtonColor.OnBrand]: primaryOnBrandClass,
  },
  [ButtonVariant.Secondary]: {
    [ButtonColor.Danger]: secondaryDangerClass,
    [ButtonColor.OnBrand]: secondaryOnBrandClass,
  },
  [ButtonVariant.Icon]: {
    [ButtonColor.Danger]: iconDangerClass,
    [ButtonColor.OnBrand]: iconOnBrandClass,
  },
  [ButtonVariant.Text]: {
    [ButtonColor.Default]: textDefaultClass,
    [ButtonColor.Danger]: textDangerClass,
    [ButtonColor.OnBrand]: textOnBrandClass,
  },
  [ButtonVariant.Inline]: {
    [ButtonColor.Default]: inlineDefaultClass,
    [ButtonColor.Danger]: inlineDangerClass,
    [ButtonColor.OnBrand]: inlineOnBrandClass,
  },
};

/*
 * Defined after the icon tone classes so its border reset wins over theirs.
 */
export const iconGhostClass = style({
  border: 'none',
});

/**
 * Underline applies only to default slot text, not the leading icon.
 * whiteSpace must be redeclared here: the theme normalize sets
 * `span { white-space: pre-line }`, which beats the value inherited
 * from the button element.
 */
export const textLikeLabelClass = style({
  textDecoration: 'underline',
  textUnderlineOffset: spacingVars.xs,
  whiteSpace: 'nowrap',
});

export const iconClass = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  flexShrink: 0,
  textDecoration: 'none',
});

export const iconEndClass = style({
  order: 1,
});

/** Icon-only button: size SM vs MD differs only by padding (xs vs md). */
export const iconSizeClasses = styleVariants({
  [ButtonSize.SM]: {
    minWidth: 'auto',
    width: 'auto',
    height: 'auto',
    padding: spacingVars.xs,
    gap: 0,
  },
  [ButtonSize.MD]: {
    minWidth: 'auto',
    width: 'auto',
    height: 'auto',
    padding: spacingVars.md,
    gap: 0,
  },
});

/** Primary, Secondary and Text small size: shorter height with tighter vertical padding. */
export const smSizeClass = style({
  height: SOLID_SM_HEIGHT,
  padding: `${spacingVars.xs} ${spacingVars.md}`,
});

export const disabledTooltipTriggerClass = style({
  display: 'inline-flex',
  cursor: 'not-allowed',
});

export const disabledTooltipButtonClass = style({
  pointerEvents: 'none',
});
